import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  MdArrowBack, MdPlayArrow, MdPause, MdSkipNext, MdSkipPrevious,
  MdFastRewind, MdFastForward, MdFlag, MdWarningAmber,
} from 'react-icons/md';
import { AppContext } from '../contexts/AppContext';
import { useProfile } from '../contexts/ProfileContext';
import { fetchScenario, fetchBudgetOverview, fetchTodayFeed } from '../services/backendAPI';

// Scenario Runner (plan 150) — a DEV-ONLY demo tool. Loads a persona's timeline,
// then drives a simulated "as-of" date so you can watch the budget fill, guardrails
// trip, and shortfall/fraud items appear as their transactions post — live, as a
// simulated clock advances. All computation stays server-side per asOf (this screen
// only picks the date and renders what comes back). It never touches the normal
// customer flow.

const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

const CARD_INK = 'var(--text-dark)';
const CARD_MUTED = 'var(--text-light)';
const BAR_TRACK = 'var(--border-color)';
const ACCENT = '#2FBF00';

// How long each play tick lasts (ms). Floored so the tick rate is capped and the
// debounced re-fetch has time to settle between advances.
const TICK_MS = 900;
// Debounce for the per-asOf re-fetch while scrubbing/playing.
const FETCH_DEBOUNCE_MS = 220;

// Two stepping modes with three speeds each (plan 150): sim-days per tick for long
// spans, or transactions per tick to watch the ledger get consumed a few at a time.
const SPEEDS = {
  days: [1, 4, 16],
  txns: [1, 5, 20],
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── UTC-safe date helpers (dates are plain YYYY-MM-DD strings) ─────────────────
function toUTC(s) {
  const [y, m, d] = String(s || '').split('-').map(Number);
  return Date.UTC(y, (m || 1) - 1, d || 1);
}
function addDays(s, n) {
  const t = toUTC(s) + n * 86400000;
  return new Date(t).toISOString().slice(0, 10);
}
function daysBetween(a, b) {
  return Math.round((toUTC(b) - toUTC(a)) / 86400000);
}
function longLabel(s) {
  const [y, m, d] = String(s || '').split('-').map(Number);
  if (!y || !m || !d) return '—';
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

// Bar color by pace: over = red, projected-over = amber, else green (mirrors Budget).
function paceColor(row) {
  if (row.limit == null) return '#9aa0a6';
  if (row.pct >= 100) return 'var(--td-red)';
  if (row.projectedOver) return '#d68910';
  return ACCENT;
}

const SEVERITY_DOT = { high: 'var(--td-red)', medium: '#d68910', low: '#4a90d9' };
const KIND_ICON = {
  month_rollover: '📅', limit_crossed: '📊', shortfall: '⚠️', anomaly: '🔎', fraud: '🚨',
};

function LiveBudgetCard({ overview }) {
  if (!overview) return null;
  const { summary = {}, categories = [], month = {} } = overview;
  const base = summary.totalBudget > 0 ? summary.totalBudget
    : (summary.income > 0 ? summary.income : Math.max(summary.spent || 0, 1));
  const spentPct = Math.min(100, Math.round(((summary.spent || 0) / base) * 100));
  return (
    <div className="ins-card">
      <div style={{ fontSize: 12, color: CARD_MUTED, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {month.label || 'This month'} · live budget
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '2px 0 10px' }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: CARD_INK }}>{money(summary.spent)}</div>
        <div style={{ fontSize: 13, color: CARD_MUTED }}>spent this month</div>
        <div style={{ marginLeft: 'auto', fontSize: 12, color: CARD_MUTED }}>
          {money(summary.remainingToSpend)} left
        </div>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: BAR_TRACK, overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 4, width: `${spentPct}%`, background: ACCENT }} />
      </div>

      {categories.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {categories.slice(0, 6).map((row) => {
            const noLimit = row.limit == null;
            const pct = noLimit ? 0 : Math.round((row.spent / row.limit) * 100);
            const barPct = Math.min(100, pct);
            const over = !noLimit && pct >= 100;
            return (
              <div key={row.category}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
                  <span style={{ fontWeight: 700, color: CARD_INK }}>{row.category}</span>
                  <span style={{ color: over ? 'var(--td-red)' : CARD_MUTED, fontWeight: over ? 700 : 400 }}>
                    {noLimit ? `${money(row.spent)} spent` : `${money(row.spent)} of ${money(row.limit)}`}
                    {over ? ' · over' : ''}
                  </span>
                </div>
                <div style={{ height: 7, borderRadius: 4, background: BAR_TRACK, overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 4, width: `${barPct}%`, background: paceColor(row) }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TodayFeedCard({ feed }) {
  if (!feed || !feed.length) {
    return (
      <div className="ins-card" style={{ textAlign: 'center', color: CARD_MUTED, fontSize: 13 }}>
        Nothing in the feed yet at this point in time.
      </div>
    );
  }
  return (
    <div className="ins-card">
      <div style={{ fontSize: 12, color: CARD_MUTED, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
        Today feed
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {feed.slice(0, 6).map((e) => (
          <div key={e.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{
              width: 8, height: 8, borderRadius: 4, marginTop: 5, flexShrink: 0,
              background: SEVERITY_DOT[e.severity] || '#9aa0a6',
            }} />
            <span style={{ fontSize: 13, color: CARD_INK, lineHeight: 1.3 }}>{e.title}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const ScenarioScreen = ({ onNavigate, onBack }) => {
  const { sessionId, sessionSummary } = useContext(AppContext);
  const { loadedProfileData } = useProfile();
  const profileName = loadedProfileData?.name || sessionSummary?.customerName;

  const [timeline, setTimeline] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(false);

  const [simDate, setSimDate] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [mode, setMode] = useState('txns');   // 'days' | 'txns'
  const [speedIdx, setSpeedIdx] = useState(0); // index into SPEEDS[mode]

  const [overview, setOverview] = useState(null);
  const [feed, setFeed] = useState([]);

  // Flattened per-transaction dates (one entry per txn) so txns-mode stepping and the
  // "consumed X of Y" counter are exact even when several txns share a date.
  const expanded = useMemo(() => {
    if (!timeline) return [];
    const out = [];
    for (const d of timeline.dates) {
      const n = timeline.countsByDate[d] || 0;
      for (let i = 0; i < n; i += 1) out.push(d);
    }
    return out;
  }, [timeline]);

  const minDate = timeline?.min || null;
  const maxDate = timeline?.max || null;
  // One day before the first txn = the "empty" start (0 consumed).
  const startDate = minDate ? addDays(minDate, -1) : null;
  const spanDays = startDate && maxDate ? daysBetween(startDate, maxDate) : 0;

  const consumed = useMemo(() => {
    if (!simDate) return 0;
    let c = 0;
    for (const d of expanded) { if (d <= simDate) c += 1; else break; }
    return c;
  }, [expanded, simDate]);

  // ── Bootstrap: load the timeline + milestones for the session ────────────────
  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    setLoading(true);
    setPlaying(false);
    (async () => {
      const r = await fetchScenario(sessionId);
      if (cancelled) return;
      setLoading(false);
      if (r?.ready && r.timeline?.dates?.length) {
        setTimeline(r.timeline);
        setMilestones(r.milestones || []);
        setSimDate(r.timeline.min ? addDays(r.timeline.min, -1) : null); // start empty
      } else {
        setTimeline(null);
        setMilestones([]);
        setSimDate(null);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // ── Debounced re-fetch of the moving views whenever the sim date changes ──────
  const fetchTimer = useRef(null);
  useEffect(() => {
    if (!sessionId || !simDate || !minDate || !maxDate) return undefined;
    // Below the first txn the world is empty; clamp the query to the real window.
    const asOf = simDate < minDate ? addDays(minDate, -1) : (simDate > maxDate ? maxDate : simDate);
    clearTimeout(fetchTimer.current);
    fetchTimer.current = setTimeout(async () => {
      const [ov, td] = await Promise.all([
        fetchBudgetOverview(sessionId, asOf),
        fetchTodayFeed(sessionId, asOf),
      ]);
      if (ov?.ready) setOverview(ov.overview);
      if (td?.ready) setFeed(td.feed || []);
    }, FETCH_DEBOUNCE_MS);
    return () => clearTimeout(fetchTimer.current);
  }, [sessionId, simDate, minDate, maxDate]);

  // Advance the sim date by one tick in `dir` (+1 forward, -1 back), honoring the
  // current mode + speed. Clamps to [startDate, maxDate]; returns the same string
  // when already at a bound (so the caller can stop playback at the end).
  const step = useCallback((prev, dir) => {
    if (!timeline) return prev;
    const speed = SPEEDS[mode][speedIdx];
    if (mode === 'days') {
      const next = addDays(prev, dir * speed);
      if (next <= startDate) return startDate;
      if (next >= maxDate) return maxDate;
      return next;
    }
    // txns mode — move to the date of the (consumed ± speed)-th transaction.
    let c = 0;
    for (const d of expanded) { if (d <= prev) c += 1; else break; }
    const target = Math.max(0, Math.min(expanded.length, c + dir * speed));
    if (target <= 0) return startDate;
    return expanded[target - 1];
  }, [timeline, mode, speedIdx, expanded, startDate, maxDate]);

  // ── Play loop ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => {
      setSimDate((prev) => {
        const next = step(prev, +1);
        if (next === prev || next >= maxDate) setPlaying(false);
        return next;
      });
    }, TICK_MS);
    return () => clearInterval(id);
  }, [playing, step, maxDate]);

  const onScrub = (v) => {
    setPlaying(false);
    setSimDate(addDays(startDate, Number(v)));
  };
  const jumpTo = (date) => { setPlaying(false); setSimDate(date); };
  const reset = () => { setPlaying(false); setSimDate(startDate); };

  const scrubValue = simDate && startDate ? Math.max(0, Math.min(spanDays, daysBetween(startDate, simDate))) : 0;

  return (
    <div className="screen insights-screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate && onNavigate('services'))} aria-label="Back">
            <MdArrowBack />
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <h1 className="bh-flow-title">Scenario Runner</h1>
            {profileName && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', fontWeight: 500, marginTop: 2 }}>{profileName}</div>
            )}
          </div>
        </div>
      </header>

      <div className="scroll-content gray-bg ins-scroll">
        <div style={{ fontSize: 12, color: 'var(--text-light)', textAlign: 'center', marginBottom: 2 }}>
          replay this persona's transactions over time — watch the budget fill and events fire
        </div>

        {!sessionId && (
          <div className="ins-card" style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
            Open a profile to run a scenario.
          </div>
        )}

        {sessionId && loading && !timeline && (
          <div className="ins-card" style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
            IVA is building the timeline…
          </div>
        )}

        {sessionId && !loading && !timeline && (
          <div className="ins-card" style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
            No transactions to replay for this profile.
          </div>
        )}

        {timeline && (
          <>
            {/* ── Controls ── */}
            <div className="ins-card">
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: CARD_INK }}>{longLabel(simDate)}</div>
                <div style={{ marginLeft: 'auto', fontSize: 12, color: CARD_MUTED }}>
                  consumed {consumed} of {timeline.total} txns
                </div>
              </div>

              <input
                type="range" min={0} max={spanDays} value={scrubValue}
                onMouseDown={() => setPlaying(false)}
                onTouchStart={() => setPlaying(false)}
                onInput={(e) => onScrub(e.target.value)}
                onChange={(e) => onScrub(e.target.value)}
                style={{ width: '100%', margin: '10px 0 4px', accentColor: ACCENT, cursor: 'pointer' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: CARD_MUTED }}>
                <span>{longLabel(minDate)}</span>
                <span>{longLabel(maxDate)}</span>
              </div>

              {/* Transport: ⏮ start | ⏪ step back | ▶/⏸ | ⏩ step forward | ⏭ end */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12, alignItems: 'center' }}>
                <button type="button" onClick={reset} aria-label="Go to start"
                  style={ctrlBtn}><MdSkipPrevious size={20} /></button>
                <button type="button" onClick={() => { setPlaying(false); setSimDate((p) => step(p, -1)); }} aria-label="Step back"
                  style={ctrlBtn}><MdFastRewind size={20} /></button>
                <button type="button" onClick={() => setPlaying((p) => !p)} aria-label={playing ? 'Pause' : 'Play'}
                  style={{ ...ctrlBtn, background: playing ? '#c0392b' : ACCENT, color: '#fff', width: 46, height: 46 }}>
                  {playing ? <MdPause size={24} /> : <MdPlayArrow size={24} />}
                </button>
                <button type="button" onClick={() => { setPlaying(false); setSimDate((p) => step(p, +1)); }} aria-label="Step forward"
                  style={ctrlBtn}><MdFastForward size={20} /></button>
                <button type="button" onClick={() => { setPlaying(false); setSimDate(maxDate); }} aria-label="Go to end"
                  style={ctrlBtn}><MdSkipNext size={20} /></button>
              </div>

              {/* Mode + speed */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${BAR_TRACK}` }}>
                  {['txns', 'days'].map((m) => (
                    <button key={m} type="button"
                      onClick={() => { setMode(m); setSpeedIdx(0); }}
                      style={{
                        ...segBtn, background: mode === m ? '#1a1a1a' : 'transparent',
                        color: mode === m ? '#fff' : CARD_MUTED,
                      }}>
                      {m === 'txns' ? 'per-txn' : 'per-day'}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {SPEEDS[mode].map((s, i) => (
                    <button key={s} type="button" onClick={() => setSpeedIdx(i)}
                      style={{
                        ...pillBtn,
                        background: speedIdx === i ? ACCENT : '#f0f1f3',
                        color: speedIdx === i ? '#fff' : '#1a1a1a',
                        borderColor: speedIdx === i ? ACCENT : BAR_TRACK,
                      }}>
                      {mode === 'days' ? `${s}d` : `${s}×`}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Jump to milestone ── */}
            {milestones.length > 0 && (
              <div className="ins-card">
                <div style={{ fontSize: 12, color: CARD_MUTED, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  jump to a moment
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {milestones.map((m, i) => (
                    <button key={`${m.date}-${m.kind}-${i}`} type="button" onClick={() => jumpTo(m.date)}
                      title={m.title}
                      style={{
                        ...pillBtn, background: '#f0f1f3', color: '#1a1a1a', borderColor: BAR_TRACK,
                        display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%',
                      }}>
                      <MdFlag size={12} /> {m.date.slice(5)} {KIND_ICON[m.kind] || ''}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <LiveBudgetCard overview={overview} />
            <TodayFeedCard feed={feed} />

            {/* ── Event log (milestones; passed ones highlighted) ── */}
            {milestones.length > 0 && (
              <div className="ins-card">
                <div style={{ fontSize: 12, color: CARD_MUTED, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  event log
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {milestones.map((m, i) => {
                    const passed = simDate && m.date <= simDate;
                    return (
                      <div key={`${m.date}-${m.kind}-log-${i}`} style={{
                        display: 'flex', gap: 8, alignItems: 'flex-start',
                        opacity: passed ? 1 : 0.4,
                      }}>
                        <span style={{ fontSize: 14, flexShrink: 0 }}>{KIND_ICON[m.kind] || '•'}</span>
                        <span style={{
                          fontSize: 13, lineHeight: 1.3,
                          color: passed ? CARD_INK : CARD_MUTED,
                          fontWeight: passed && m.kind !== 'month_rollover' ? 600 : 400,
                        }}>
                          {m.title}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {milestones.some((m) => m.kind === 'fraud' || m.kind === 'shortfall') && (
                  <div style={{ marginTop: 8, fontSize: 11, color: CARD_MUTED, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <MdWarningAmber size={13} /> demo tool — no money moves; this is a read-only replay.
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const ctrlBtn = {
  width: 40, height: 40, borderRadius: 20, border: `1px solid ${BAR_TRACK}`,
  background: '#fff', color: '#1a1a1a', display: 'inline-flex', alignItems: 'center',
  justifyContent: 'center', cursor: 'pointer',
};
const segBtn = {
  border: 'none', padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
};
const pillBtn = {
  border: `1px solid ${BAR_TRACK}`, borderRadius: 8, padding: '5px 10px',
  fontSize: 12, fontWeight: 700, cursor: 'pointer',
};

export default ScenarioScreen;
